import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext, API } from '@/App';
import AdminLayout from '@/components/admin/AdminLayout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { 
  FileText, Plus, Edit, Trash2, Eye, CheckCircle, XCircle,
  Upload, Download, Loader2, AlertTriangle, Clock, User
} from 'lucide-react';

// Mapeamento centralizado de tipos jurídicos suportados pelo módulo admin.
// Espelha SUPPORTED_TERM_TYPES + TERM_TYPE_LABELS do backend (utils/admin_terms.py).
const TERM_TYPES = [
  { value: 'client', label: 'Cliente', tabLabel: 'Cliente', shortLabel: 'cliente' },
  { value: 'provider', label: 'Prestador', tabLabel: 'Prestador', shortLabel: 'prestador' },
  { value: 'privacy_policy', label: 'Política de Privacidade', tabLabel: 'Privacidade', shortLabel: 'política de privacidade' },
  { value: 'dispute_policy', label: 'Política de Contestação', tabLabel: 'Contestação', shortLabel: 'política de contestação' },
  { value: 'refund_policy', label: 'Política de Reembolso', tabLabel: 'Reembolso', shortLabel: 'política de reembolso' },
];

const getTypeMeta = (value) => TERM_TYPES.find((t) => t.value === value) || TERM_TYPES[0];

export default function AdminTerms() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [termsByType, setTermsByType] = useState({});
  const [activeTab, setActiveTab] = useState('client');
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [selectedTerm, setSelectedTerm] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    type: 'client',
    version: '',
    content: '',
    is_active: false
  });

  useEffect(() => {
    if (!user || user.user_type !== 'admin') {
      navigate('/control-portal-clube/login');
      return;
    }
    fetchTerms();
  }, [user, navigate]);

  const fetchTerms = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/admin/terms/list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Backend retorna `all` indexado por tipo (novo) + chaves legacy `client`/`provider`.
      // Preferimos `all` quando disponível para suportar os novos tipos jurídicos.
      const byType = res.data.all || {
        client: res.data.client || [],
        provider: res.data.provider || [],
      };
      // Garante chave para cada tipo conhecido (UI nunca quebra se vier vazio).
      TERM_TYPES.forEach((t) => {
        if (!byType[t.value]) byType[t.value] = [];
      });
      setTermsByType(byType);
    } catch (error) {
      toast.error('Erro ao carregar termos');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!formData.version.trim()) {
      toast.error('Informe a versão');
      return;
    }
    if (!formData.content.trim()) {
      toast.error('Informe o conteúdo');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/terms/create`, {
        ...formData,
        type: activeTab
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Termos criados com sucesso!');
      setShowCreateModal(false);
      resetForm();
      fetchTerms();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar termos');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!formData.content.trim()) {
      toast.error('Informe o conteúdo');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/admin/terms/${selectedTerm.id}`, {
        content: formData.content,
        is_active: formData.is_active
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Termos atualizados com sucesso!');
      setShowEditModal(false);
      resetForm();
      fetchTerms();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar termos');
    } finally {
      setSaving(false);
    }
  };

  const handleActivate = async (term) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/admin/terms/${term.id}/activate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Versão ${term.version} ativada!`);
      fetchTerms();
    } catch (error) {
      toast.error('Erro ao ativar termos');
    }
  };

  const handleDelete = async (term) => {
    if (!window.confirm(`Tem certeza que deseja excluir a versão ${term.version}?`)) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/admin/terms/${term.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Termos excluídos!');
      fetchTerms();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir termos');
    }
  };

  const handleUploadPdf = async (term, file) => {
    const formData = new FormData();
    formData.append('file', file);

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/terms/${term.id}/upload-pdf`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success('PDF enviado com sucesso!');
      fetchTerms();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao enviar PDF');
    }
  };

  const handleDeletePdf = async (term) => {
    if (!window.confirm('Remover o PDF anexado?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/admin/terms/${term.id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('PDF removido!');
      fetchTerms();
    } catch (error) {
      toast.error('Erro ao remover PDF');
    }
  };

  const resetForm = () => {
    setFormData({
      type: activeTab,
      version: '',
      content: '',
      is_active: false
    });
    setSelectedTerm(null);
  };

  const openEditModal = (term) => {
    setSelectedTerm(term);
    setFormData({
      type: term.type,
      version: term.version,
      content: term.content,
      is_active: term.is_active
    });
    setShowEditModal(true);
  };

  const openPreviewModal = (term) => {
    setSelectedTerm(term);
    setShowPreviewModal(true);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const TermCard = ({ term }) => (
    <Card className={`p-4 ${term.is_active ? 'border-green-300 bg-green-50' : ''}`}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <FileText className="w-5 h-5 text-slate-500" />
          <span className="font-semibold">Versão {term.version}</span>
          {term.is_active && (
            <Badge className="bg-green-100 text-green-700">
              <CheckCircle className="w-3 h-3 mr-1" />
              Ativa
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => openPreviewModal(term)}
            title="Visualizar"
          >
            <Eye className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => openEditModal(term)}
            title="Editar"
          >
            <Edit className="w-4 h-4" />
          </Button>
          {!term.is_active && (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleActivate(term)}
                title="Ativar"
                className="text-green-600 hover:text-green-700"
              >
                <CheckCircle className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleDelete(term)}
                title="Excluir"
                className="text-red-600 hover:text-red-700"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="text-sm text-slate-500 space-y-1 mb-3">
        <div className="flex items-center gap-2">
          <Clock className="w-3 h-3" />
          <span>Criado em: {formatDate(term.created_at)}</span>
        </div>
        {term.updated_at !== term.created_at && (
          <div className="flex items-center gap-2">
            <Clock className="w-3 h-3" />
            <span>Atualizado em: {formatDate(term.updated_at)}</span>
          </div>
        )}
      </div>

      {/* PDF Section */}
      <div className="border-t pt-3 mt-3">
        <div className="flex items-center justify-between">
          <span className="text-sm text-slate-600">Arquivo PDF:</span>
          {term.file_url ? (
            <div className="flex items-center gap-2">
              <a
                href={`${API.replace('/api', '')}${term.file_url}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-primary hover:underline flex items-center gap-1"
              >
                <Download className="w-3 h-3" />
                Baixar PDF
              </a>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleDeletePdf(term)}
                className="text-red-600 hover:text-red-700 h-6 px-2"
              >
                <Trash2 className="w-3 h-3" />
              </Button>
            </div>
          ) : (
            <label className="cursor-pointer">
              <input
                type="file"
                accept=".pdf"
                className="hidden"
                onChange={(e) => {
                  if (e.target.files[0]) {
                    handleUploadPdf(term, e.target.files[0]);
                  }
                }}
              />
              <span className="text-sm text-primary hover:underline flex items-center gap-1">
                <Upload className="w-3 h-3" />
                Enviar PDF
              </span>
            </label>
          )}
        </div>
      </div>

      {/* Preview snippet */}
      <div className="mt-3 p-2 bg-slate-100 rounded text-xs text-slate-600 line-clamp-3">
        {term.content.substring(0, 200)}...
      </div>
    </Card>
  );

  if (loading) {
    return (
      <AdminLayout title="Termos de Uso" subtitle="Carregando...">
        <div className="space-y-4">
          <Skeleton className="h-40" />
          <Skeleton className="h-40" />
        </div>
      </AdminLayout>
    );
  }

  const currentTerms = termsByType[activeTab] || [];
  const typeMeta = getTypeMeta(activeTab);
  const typeLabel = typeMeta.label;

  return (
    <AdminLayout>
      <div className="space-y-6 max-w-5xl">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Termos de Uso</h1>
            <p className="text-slate-500">Gerencie os termos de uso do sistema</p>
          </div>
          <Button onClick={() => {
            resetForm();
            setShowCreateModal(true);
          }}>
            <Plus className="w-4 h-4 mr-2" />
            Nova Versão
          </Button>
        </div>

        {/* Info Alert */}
        <Alert className="bg-blue-50 border-blue-200">
          <FileText className="h-4 w-4 text-blue-600" />
          <AlertDescription className="text-blue-800">
            Os termos ativos são automaticamente exibidos durante o cadastro de novos usuários.
            A versão aceita é registrada junto com a data e hora do aceite.
          </AlertDescription>
        </Alert>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-2 md:grid-cols-5 gap-1">
            {TERM_TYPES.map((t) => (
              <TabsTrigger
                key={t.value}
                value={t.value}
                className="flex items-center gap-1 text-xs md:text-sm"
                data-testid={`tab-terms-${t.value}`}
              >
                <FileText className="w-3.5 h-3.5" />
                <span className="truncate">{t.tabLabel}</span>
                <Badge variant="outline" className="ml-1">{(termsByType[t.value] || []).length}</Badge>
              </TabsTrigger>
            ))}
          </TabsList>

          {TERM_TYPES.map((t) => {
            const list = termsByType[t.value] || [];
            return (
              <TabsContent key={t.value} value={t.value} className="mt-6">
                {list.length === 0 ? (
                  <Card className="p-8 text-center">
                    <FileText className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                    <h3 className="font-semibold mb-2">Nenhum termo cadastrado</h3>
                    <p className="text-sm text-slate-500 mb-4">
                      Crie a primeira versão de {t.shortLabel}.
                    </p>
                    <Button onClick={() => {
                      resetForm();
                      setShowCreateModal(true);
                    }}>
                      <Plus className="w-4 h-4 mr-2" />
                      Criar Termos
                    </Button>
                  </Card>
                ) : (
                  <div className="grid gap-4">
                    {list.map((term) => (
                      <TermCard key={term.id} term={term} />
                    ))}
                  </div>
                )}
              </TabsContent>
            );
          })}
        </Tabs>
      </div>

      {/* Create Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nova Versão de Termos ({typeLabel})</DialogTitle>
            <DialogDescription>
              Crie uma nova versão dos termos de uso para {typeLabel.toLowerCase()}s.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="version">Versão *</Label>
                <Input
                  id="version"
                  placeholder="Ex: 1.1, 2.0"
                  value={formData.version}
                  onChange={(e) => setFormData({ ...formData, version: e.target.value })}
                />
              </div>
              <div className="flex items-center gap-3 pt-6">
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                />
                <Label>Ativar imediatamente</Label>
              </div>
            </div>

            <div>
              <Label htmlFor="content">Conteúdo *</Label>
              <Textarea
                id="content"
                placeholder="Digite o conteúdo dos termos de uso..."
                className="min-h-[400px] font-mono text-sm"
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
              />
              <p className="text-xs text-slate-500 mt-1">
                Suporta formatação Markdown. Use # para títulos, ## para subtítulos, etc.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>
              Cancelar
            </Button>
            <Button onClick={handleCreate} disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Criar Termos
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Editar Termos - Versão {selectedTerm?.version}</DialogTitle>
            <DialogDescription>
              Edite o conteúdo dos termos de uso. A versão não pode ser alterada.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="flex items-center gap-3">
              <Switch
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
              />
              <Label>Termos ativos</Label>
              {formData.is_active && (
                <Badge className="bg-green-100 text-green-700">
                  <CheckCircle className="w-3 h-3 mr-1" />
                  Esta versão será exibida aos usuários
                </Badge>
              )}
            </div>

            <div>
              <Label htmlFor="edit-content">Conteúdo *</Label>
              <Textarea
                id="edit-content"
                className="min-h-[400px] font-mono text-sm"
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditModal(false)}>
              Cancelar
            </Button>
            <Button onClick={handleUpdate} disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Salvar Alterações
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Modal */}
      <Dialog open={showPreviewModal} onOpenChange={setShowPreviewModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5" />
              Visualização - Versão {selectedTerm?.version}
              {selectedTerm?.is_active && (
                <Badge className="bg-green-100 text-green-700 ml-2">Ativa</Badge>
              )}
            </DialogTitle>
          </DialogHeader>

          <div className="py-4">
            <div className="prose prose-sm max-w-none bg-white border rounded-lg p-6 max-h-[60vh] overflow-y-auto">
              <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed">
                {selectedTerm?.content}
              </pre>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPreviewModal(false)}>
              Fechar
            </Button>
            {selectedTerm && !selectedTerm.is_active && (
              <Button onClick={() => {
                handleActivate(selectedTerm);
                setShowPreviewModal(false);
              }}>
                <CheckCircle className="w-4 h-4 mr-2" />
                Ativar Esta Versão
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
